"use client";

import Link from "next-shims/Link";
import { useParams } from "next-shims/navigation";
import {
  Box,
  Container,
  Divider,
  Stack,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { useI18n } from "i18n/I18nProvider";

export default function Footer() {
  const params = useParams() as { locale?: string };
  const locale = params?.locale || "el";
  const { t } = useI18n();

  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        borderTop: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* subtle divider line (optional, but premium) */}
      <Divider sx={{ opacity: 0.6 }} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={2.2}>
          {/* Row 1: Copyright + Links */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} {t("brand_name")}. {t("footer_rights")}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{
                justifyContent: { xs: "flex-start", md: "flex-end" },
                "& a": {
                  fontWeight: 600,
                  color: "text.secondary",
                  textDecoration: "none",
                  position: "relative",
                },
                "& a::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -2,
                  width: "100%",
                  height: 1,
                  bgcolor: "currentColor",
                  opacity: 0.18,
                  transition: "opacity .2s ease",
                },
                "& a:hover::after": {
                  opacity: 0.55,
                },
              }}
            >
              <MuiLink component={Link} href={`/${locale}/contact`}>
                {t("nav_contact")}
              </MuiLink>
              <MuiLink component={Link} href={`/${locale}/reviews`}>
                {t("nav_reviews")}
              </MuiLink>
              <MuiLink component={Link} href={`/${locale}/privacy-policy`}>
                {t("nav_privacy")}
              </MuiLink>
              <MuiLink component={Link} href={`/${locale}/terms`}>
                {t("nav_terms")}
              </MuiLink>
            </Stack>
          </Stack>

          {/* Row 2: Brainstorm Visions credit */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{
              pt: 1.2,
              borderTop: "1px solid",
              borderColor: "divider",
              opacity: 0.92,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t("footer_credit_prefix")}{" "}
              <MuiLink
                href="https://brainstorm-visions.com/"
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  "&:hover": { opacity: 0.85 },
                }}
              >
                Brainstorm Visions
              </MuiLink>
            </Typography>

            {/* μικρό “hint” για premium vibe */}
            <Typography variant="caption" color="text.secondary">
              {t("footer_credit_suffix")}
            </Typography>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
